import { Authenticated, Unauthenticated } from '../utils/auth/auth'
import { getCurrentUser } from '../utils/auth/UserProvider'

import { UUID } from 'crypto'

import LoginSignup from '@/components/LoginSignup'
import { getEffectiveOrgUuid } from '@/utils/utils'
import Loading from '@/components/Loading'
import ErrorMessage from '@/components/ErrorMessage'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useCallback, useRef, useMemo } from 'react'
import { useOnboardingStatus } from '@/pages/onboarding/hooks/useOnboardingStatus'
import {
  getPostLoginRedirect,
  clearPostLoginRedirect,
  markPostLoginRedirectUsed,
} from '@/utils/postLoginRedirect'

export default function LandingPage() {
  const hasNavigatedRef = useRef(false)
  const navigationAttemptedRef = useRef(false)

  const { isLoading, user, token, orgs: userOrgs } = getCurrentUser()
  const effectiveOrgUuid = getEffectiveOrgUuid(user, userOrgs)

  const { isOnboarding, isLoading: statusLoading } = useOnboardingStatus()

  const {
    orgData,
    isLoading: orgsLoading,
    isError,
  } = useCurrentUserOrgs(token as string, user, [effectiveOrgUuid] as UUID[])

  const navigate = useNavigate()
  const location = useLocation()

  // Reset navigation flags when location changes
  useEffect(() => {
    hasNavigatedRef.current = false
    navigationAttemptedRef.current = false
  }, [location.pathname])

  // Memoize the overall loading state to prevent unnecessary re-renders
  const isDataLoading = useMemo(() => {
    return isLoading || orgsLoading || statusLoading
  }, [isLoading, orgsLoading, statusLoading])

  // Memoize authentication state
  const isAuthenticated = useMemo(() => {
    return !!(user && token)
  }, [user, token])

  // Helper function to safely navigate
  const safeNavigate = useCallback(
    (route: string) => {
      if (hasNavigatedRef.current) return

      try {
        hasNavigatedRef.current = true
        navigate(route, { replace: true })
      } catch (error) {
        console.error(`Navigation to ${route} failed`)
        hasNavigatedRef.current = false
      }
    },
    [navigate]
  )

  // Memoized navigation logic - only runs when actual decision-making data changes
  const handleNavigation = useCallback(() => {
    // Prevent duplicate navigation attempts
    if (hasNavigatedRef.current || navigationAttemptedRef.current) {
      return
    }

    // Only run navigation logic when we're on the landing page
    if (location.pathname !== '/') {
      return
    }

    // If user is not authenticated, we don't need to do anything - let the component render normally
    if (!isAuthenticated) {
      navigationAttemptedRef.current = true // Mark as attempted so we don't keep trying
      return
    }

    // Don't attempt navigation if data is still loading (only for authenticated users)
    if (isDataLoading) {
      return
    }

    // A transient API failure (e.g. /orgs 500) leaves orgData empty while
    // isDataLoading flips false. Without this guard we'd mistake that for
    // "user has no org" and force-redirect to /onboarding before the
    // error view can render.
    if (isError) {
      return
    }

    // Mark that we've attempted navigation to prevent loops
    navigationAttemptedRef.current = true

    // All data is loaded and user is authenticated, make navigation decisions

    // If onboarding is active, navigate to onboarding
    if (isOnboarding) {
      safeNavigate('/onboarding')
      return
    }

    // Check if user needs onboarding (no org)
    if (!orgData || orgData.length === 0) {
      safeNavigate('/onboarding')
      return
    }

    // Honour a deep link the user was trying to reach before logging in
    // (e.g. an email CTA to /profile/accounts). Invite paths are handled
    // earlier by AuthenticatedRoute, so they're excluded here. Only fires
    // once — cleared on use to avoid hijacking later visits to /.
    const postLoginRedirect = getPostLoginRedirect()
    if (
      postLoginRedirect &&
      postLoginRedirect !== '/' &&
      !postLoginRedirect.startsWith('/invite')
    ) {
      markPostLoginRedirectUsed()
      clearPostLoginRedirect()
      safeNavigate(postLoginRedirect)
      return
    }

    if (orgData[0] && !orgData[0].description) {
      // Has org but no profile
      safeNavigate('/profile')
      return
    }

    // Has org and profile — send to funding requests (which handles the
    // empty-needs case with its own CTA instead of the landing-page spinner).
    safeNavigate('/funding-requests')
  }, [
    location.pathname,
    isAuthenticated,
    isDataLoading,
    isError,
    isOnboarding,
    orgData,
    safeNavigate,
  ])

  useEffect(() => {
    handleNavigation()
  }, [handleNavigation])

  if (isError) {
    return <ErrorMessage />
  }

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        <Loading />
      </Authenticated>
    </>
  )
}
